import { useEffect } from 'react';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const solutionsJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: '픽스코프 솔루션 | Fixcope Solutions',
  url: `${siteUrl}/solutions`,
  description: '픽스코프의 비즈니스 솔루션: 엔터프라이즈 솔루션, 디지털 전환, 데이터 관리, 협업 도구.',
  inLanguage: 'ko-KR',
};

export default function SolutionsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const solutions = [
    {
      icon: 'ri-building-line',
      title: 'Enterprise Solutions',
      description: 'Comprehensive enterprise-grade solutions designed to streamline operations, enhance collaboration, and drive organizational efficiency',
      features: ['ERP Systems', 'CRM Platforms', 'Workflow Automation', 'Document Management']
    },
    {
      icon: 'ri-smartphone-line',
      title: 'Digital Transformation',
      description: 'End-to-end digital transformation services that modernize your business processes and create seamless digital experiences',
      features: ['Process Digitization', 'Legacy System Migration', 'Mobile-First Strategy', 'API Integration']
    },
    {
      icon: 'ri-database-2-line',
      title: 'Data Management',
      description: 'Advanced data management solutions that ensure data quality, accessibility, and security across your organization',
      features: ['Data Warehousing', 'ETL Processes', 'Master Data Management', 'Data Governance']
    },
    {
      icon: 'ri-team-line',
      title: 'Collaboration Tools',
      description: 'Modern collaboration platforms that connect teams, improve communication, and boost productivity across locations',
      features: ['Team Communication', 'Project Management', 'Video Conferencing', 'File Sharing']
    }
  ];

  const benefits = [
    {
      icon: 'ri-speed-up-line',
      title: 'Increased Efficiency',
      description: 'Streamline operations and automate repetitive tasks to boost productivity by up to 40%'
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Cost Reduction',
      description: 'Optimize resources and reduce operational costs through intelligent automation and process improvement'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Enhanced Security',
      description: 'Protect your business with enterprise-grade security measures and compliance frameworks'
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Scalable Growth',
      description: 'Build flexible solutions that grow with your business and adapt to changing market demands'
    }
  ];

  const useCases = [
    {
      industry: 'Financial Services',
      challenge: 'Legacy systems causing operational inefficiencies',
      solution: 'Implemented cloud-based core banking system with real-time processing',
      result: '60% faster transaction processing and 45% cost reduction',
      image: 'https://public.readdy.ai/ai/img_res/be8e2413ce6d3860f7d29adbf6e9e969.jpg'
    },
    {
      industry: 'Healthcare',
      challenge: 'Disconnected patient data across multiple systems',
      solution: 'Developed integrated electronic health records platform',
      result: '80% improvement in data accessibility and patient care coordination',
      image: 'https://readdy.ai/api/search-image?query=advanced%20healthcare%20technology%20center%20with%20medical%20professionals%20using%20digital%20tablets%20and%20electronic%20health%20record%20systems%2C%20modern%20hospital%20environment%20with%20integrated%20medical%20devices%2C%20clean%20white%20and%20blue%20color%20scheme%2C%20futuristic%20medical%20workspace%20with%20patient%20data%20visualization&width=600&height=400&seq=health001&orientation=landscape'
    },
    {
      industry: 'Retail',
      challenge: 'Fragmented customer experience across channels',
      solution: 'Created omnichannel retail platform with unified customer view',
      result: '35% increase in customer satisfaction and 50% boost in online sales',
      image: 'https://readdy.ai/api/search-image?query=modern%20retail%20technology%20store%20with%20interactive%20digital%20displays%20and%20smart%20shopping%20experiences%2C%20customers%20using%20mobile%20apps%20for%20seamless%20checkout%2C%20contemporary%20retail%20space%20with%20blue%20accent%20lighting%2C%20omnichannel%20shopping%20environment%20with%20integrated%20technology%20solutions&width=600&height=400&seq=retail001&orientation=landscape'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="픽스코프 솔루션 | Fixcope - 엔터프라이즈·디지털 전환·데이터 관리"
        description="픽스코프의 혁신적인 비즈니스 솔루션을 소개합니다. 엔터프라이즈 솔루션, 디지털 전환, 데이터 관리, 협업 도구로 비즈니스 성과를 극대화하세요."
        keywords="픽스코프 솔루션, 엔터프라이즈 솔루션, 디지털 전환, 데이터 관리, Fixcope solutions"
        canonical="/solutions"
        ogTitle="픽스코프 솔루션 | Fixcope"
        ogDescription="엔터프라이즈·디지털 전환·데이터 관리 솔루션으로 비즈니스 성과를 극대화하세요."
        lang="ko"
        jsonLd={solutionsJsonLd}
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-8 bg-gradient-to-br from-[#0066CC] to-[#0052A3] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="max-w-[1400px] mx-auto relative z-10">
          <div className="text-center text-white">
            <div className="inline-flex items-center space-x-2 mb-6">
              <i className="ri-lightbulb-line text-white/80"></i>
              <span className="text-sm font-semibold uppercase tracking-wider whitespace-nowrap">
                Our Solutions
              </span>
            </div>
            <h1 className="text-6xl font-bold mb-6">
              Innovative Business Solutions
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
              Tailored technology solutions that address your unique business challenges and unlock new opportunities for growth and innovation
            </p>
          </div>
        </div>
      </section>

      {/* Solutions Overview */}
      <section className="py-24 px-8 bg-[#F8F9FA]">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-[#1A2332] mb-6">
              Comprehensive Solution Portfolio
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              From enterprise systems to digital transformation, we deliver solutions that drive measurable business outcomes
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {solutions.map((solution, index) => (
              <div
                key={index}
                className="bg-white rounded-3xl p-10 hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] cursor-pointer border border-gray-100"
              >
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[#E3F2FD] to-[#BBDEFB] text-[#0066CC] flex items-center justify-center mb-6">
                  <i className={`${solution.icon} text-3xl`}></i>
                </div>
                <h3 className="text-3xl font-bold text-[#1A2332] mb-4">
                  {solution.title}
                </h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {solution.description}
                </p>
                <div className="space-y-3">
                  {solution.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center space-x-3">
                      <i className="ri-checkbox-circle-fill text-[#0066CC]"></i>
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 px-8 bg-white">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-[#1A2332] mb-6">
              Why Choose Our Solutions
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Proven benefits that deliver real value to your organization
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="text-center p-8 rounded-2xl bg-gradient-to-br from-[#E3F2FD] to-[#BBDEFB] hover:shadow-xl transition-all duration-300"
              >
                <div className="w-16 h-16 rounded-full bg-white text-[#0066CC] flex items-center justify-center mx-auto mb-6">
                  <i className={`${benefit.icon} text-3xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-[#1A2332] mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-24 px-8 bg-[#F8F9FA]">
        <div className="max-w-[1400px] mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-[#1A2332] mb-6">
              Real-World Success Stories
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              See how our solutions have transformed businesses across different industries
            </p>
          </div>

          <div className="space-y-12">
            {useCases.map((useCase, index) => (
              <div
                key={index}
                className="bg-white rounded-3xl overflow-hidden hover:shadow-2xl transition-all duration-300"
              >
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                  <div className="relative h-80 lg:h-auto">
                    <img
                      src={useCase.image}
                      alt={useCase.industry}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="p-12">
                    <div className="inline-block px-4 py-2 bg-gradient-to-br from-[#E3F2FD] to-[#BBDEFB] text-[#0066CC] rounded-full text-sm font-semibold mb-6 whitespace-nowrap">
                      {useCase.industry}
                    </div>
                    <h3 className="text-3xl font-bold text-[#1A2332] mb-6">
                      Challenge
                    </h3>
                    <p className="text-gray-600 mb-6 leading-relaxed">
                      {useCase.challenge}
                    </p>
                    <h3 className="text-3xl font-bold text-[#1A2332] mb-6">
                      Solution
                    </h3>
                    <p className="text-gray-600 mb-6 leading-relaxed">
                      {useCase.solution}
                    </p>
                    <div className="bg-gradient-to-br from-[#E3F2FD] to-[#BBDEFB] rounded-2xl p-6">
                      <h4 className="text-lg font-bold text-[#1A2332] mb-3">
                        Result
                      </h4>
                      <p className="text-[#0066CC] font-semibold text-lg">
                        {useCase.result}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-8 bg-white">
        <div className="max-w-[1400px] mx-auto">
          <div className="bg-gradient-to-br from-[#0066CC] to-[#0052A3] rounded-3xl p-16 text-center text-white">
            <h2 className="text-5xl font-bold mb-6">
              Let's Build Your Solution Together
            </h2>
            <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto leading-relaxed">
              Our team of experts is ready to help you design and implement the perfect solution for your business needs
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="px-8 py-4 bg-white text-[#0066CC] rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap">
                Request Demo
              </button>
              <button className="px-8 py-4 bg-transparent border-2 border-white text-white rounded-lg font-semibold hover:bg-white/10 transition-colors whitespace-nowrap">
                Contact Sales
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
